import React, { useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { toast } from "react-toastify";
import { FaSearch, FaPlus, FaTicketAlt } from "react-icons/fa";
import { addModeByInvitation } from "../../api/custom-mode";
import { getDiscover, getFeed } from "../../api/community";
import PublishedModeCard from "../../components/cards/PublishedModeCard";
import Rail from "../../components/community/Rail";
import { queueEvent } from "../../analytics";
import { EVENTS } from "../../analytics/events";
import CommunityLegacy from "./CommunityLegacy";
import { Button, Input, Modal, Spinner } from "../../ui";

const SORTS = [
  { value: "top", label: "الأعلى تقييماً" },
  { value: "hot", label: "الرائج" },
  { value: "new", label: "الأحدث" },
  { value: "topWeek", label: "الأفضل هذا الأسبوع" },
  { value: "rising", label: "صاعدة" },
  { value: "mostPlayed", label: "الأكثر لعباً" },
  { value: "mostQuestions", label: "الأكثر أسئلة" },
];

const iconButton =
  "inline-flex items-center justify-center w-9 h-9 rounded-lg border border-white/15 bg-white/10 text-white/85 hover:text-white hover:bg-white/15 transition-colors";

const chipClass = (active) =>
  `h-9 px-3.5 rounded-full border text-sm font-semibold transition-colors ${
    active
      ? "bg-mainButton border-mainButton text-white"
      : "bg-white/[0.05] border-white/10 text-white/70 hover:text-white hover:border-white/25"
  }`;

// Community home: curated and personal rails from /api/community/discover,
// then the whole catalog as a filterable grid. Falls back to the previous
// page when the server doesn't have that API.
const CommunityModes = () => {
  const discover = useQuery({
    queryKey: ["community", "discover"],
    queryFn: getDiscover,
    staleTime: 2 * 60 * 1000,
    retry: (count, error) => ![404, 501].includes(error?.response?.status) && count < 1,
  });
  if ([404, 501].includes(discover.error?.response?.status)) return <CommunityLegacy />;
  return <CommunityHome discover={discover} />;
};

const CommunityHome = ({ discover }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const gridRef = useRef(null);
  const [sort, setSort] = useState("top");
  const [category, setCategory] = useState(null);
  const [codeOpen, setCodeOpen] = useState(false);
  const [code, setCode] = useState("");

  const params = useMemo(() => (category ? { sort, category } : { sort }), [sort, category]);
  const feed = useInfiniteQuery({
    queryKey: ["community", "feed", params],
    queryFn: ({ pageParam }) => getFeed(params, pageParam),
    initialPageParam: undefined,
    getNextPageParam: (last) => (last?.hasMore ? last.nextCursor : undefined),
    // Keep the grid visible while another sort / category loads.
    placeholderData: keepPreviousData,
    staleTime: 60 * 1000,
  });

  // A mode can move between pages while browsing; show it once.
  const modes = useMemo(() => {
    const seen = new Set();
    const out = [];
    for (const page of feed.data?.pages || []) {
      for (const m of page?.data || []) {
        if (m?._id && !seen.has(m._id)) {
          seen.add(m._id);
          out.push(m);
        }
      }
    }
    return out;
  }, [feed.data]);

  const rails = discover.data?.rails || [];
  const categoryLabel = rails
    .find((r) => r.type === "categories")
    ?.items.find((c) => c.key === category)?.label?.ar;

  const showGrid = (next) => {
    if (next.sort) setSort(next.sort);
    if ("category" in next) setCategory(next.category || null);
    gridRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const addByCode = useMutation({
    mutationFn: () => addModeByInvitation(code.trim()),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["myCustomModes"] });
      queueEvent(EVENTS.MODE_ADDED_BY_CODE);
      setCode("");
      setCodeOpen(false);
      toast.success("تمت إضافة الفئة");
    },
    onError: (e) =>
      toast.error(
        e?.response?.status === 400
          ? "هذه الفئة مضافة لديك من قبل"
          : e?.response?.status === 404
            ? "لا توجد فئة بهذا الكود"
            : "تعذّرت الإضافة",
      ),
  });

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <h1 className="text-2xl md:text-3xl font-extrabold">المجتمع</h1>
        <div className="flex items-center gap-2">
          <Button size="sm" icon={<FaPlus />} onClick={() => navigate("/app/modes/create")}>
            إنشاء فئة
          </Button>
          <button
            onClick={() => setCodeOpen(true)}
            className={iconButton}
            aria-label="إضافة بكود"
            title="إضافة بكود"
          >
            <FaTicketAlt />
          </button>
          <button
            onClick={() => navigate("/app/community/search")}
            className={iconButton}
            aria-label="بحث"
            title="بحث"
          >
            <FaSearch />
          </button>
        </div>
      </div>

      {discover.isLoading ? (
        <div className="flex justify-center py-10 text-white/80">
          <Spinner size={28} />
        </div>
      ) : (
        <div className="space-y-8 mb-10">
          {rails.map((rail) => (
            <Rail
              key={rail.key}
              rail={rail}
              selectedCategory={category}
              onCategory={(key) => showGrid({ category: key })}
              onSeeAll={(r) =>
                showGrid({
                  sort: r.seeAll?.params?.sort || "top",
                  category: r.seeAll?.params?.category || null,
                })
              }
            />
          ))}
        </div>
      )}

      <div ref={gridRef} className="scroll-mt-20">
        <div className="flex items-center justify-between gap-3 mb-3">
          <h2 className="text-lg font-bold">
            {categoryLabel ? `فئات: ${categoryLabel}` : "كل الفئات"}
          </h2>
          {category && (
            <button
              onClick={() => setCategory(null)}
              className="text-primary text-sm font-bold hover:text-mainText transition-colors"
            >
              مسح التصنيف
            </button>
          )}
        </div>
        <div className="flex flex-wrap gap-2 mb-5">
          {SORTS.map((s) => (
            <button
              key={s.value}
              onClick={() => setSort(s.value)}
              className={chipClass(sort === s.value)}
              aria-pressed={sort === s.value}
            >
              {s.label}
            </button>
          ))}
        </div>

        {feed.isLoading ? (
          <div className="flex justify-center py-16 text-white/80">
            <Spinner size={28} />
          </div>
        ) : feed.isError && modes.length === 0 ? (
          <div className="text-center py-10 rounded-2xl border border-white/10 bg-white/[0.05]">
            <p className="text-white/70 mb-4">تعذّر تحميل الفئات</p>
            <Button variant="secondary" onClick={() => feed.refetch()}>
              إعادة المحاولة
            </Button>
          </div>
        ) : modes.length === 0 ? (
          <p className="text-white/55 text-center py-10">لا توجد فئات هنا بعد</p>
        ) : (
          <div className={feed.isPlaceholderData ? "opacity-60 transition-opacity" : "transition-opacity"}>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 sm:gap-4">
              {modes.map((m) => (
                <PublishedModeCard key={m._id} mode={m} />
              ))}
            </div>
            {feed.hasNextPage && (
              <div className="flex justify-center mt-8">
                <Button
                  variant="secondary"
                  onClick={() => feed.fetchNextPage()}
                  disabled={feed.isFetchingNextPage}
                  icon={feed.isFetchingNextPage ? <Spinner /> : null}
                >
                  عرض المزيد
                </Button>
              </div>
            )}
          </div>
        )}
      </div>

      <Modal
        open={codeOpen}
        onClose={() => setCodeOpen(false)}
        title="إضافة فئة بكود الدعوة"
        size="sm"
      >
        <Input
          type="text"
          ltr
          value={code}
          onChange={(e) => setCode(e.target.value)}
          placeholder="كود الدعوة"
          aria-label="كود الدعوة"
          autoFocus
          className="text-right mb-5"
        />
        <div className="flex gap-3">
          <Button
            className="flex-1"
            onClick={() => code.trim() && addByCode.mutate()}
            disabled={addByCode.isPending}
            icon={addByCode.isPending ? <Spinner /> : null}
          >
            إضافة
          </Button>
          <Button variant="secondary" className="flex-1" onClick={() => setCodeOpen(false)}>
            إلغاء
          </Button>
        </div>
      </Modal>
    </div>
  );
};

export default CommunityModes;
